import React, { useState, useEffect, useMemo, useImperativeHandle, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactHtmlParser from 'react-html-parser';
import TextField from '@mui/material/TextField';
import { Box } from '@mui/material';
import { InputAdornment, Typography } from '@mui/material';
import isEqual from 'lodash/isEqual';
import { setSimulationData, setLoading, setInputParams, setDisplayInput } from '../../redux/modelDataSlice';
import api from '../../api';

import { InputContainer, InputParametersContainer } from './styles';
import { recordEvent } from '../../utils/analytics/local';
import { sendAdobeEvent } from '../../utils/analytics/adobe';
import { APP_NAME } from '../../utils/constants/general';

const InputParameters = forwardRef((props, ref) => {
	const {
		boundariesPath,
		simulationNumber,
		simulationName,
		netlistPath,
		inputHeading,
		chartsDefinition,
		showInputParams
	} = props;

	const inputParametersDefinition = useSelector(
		state => state.designDefinition.partDefinition.simulations[simulationNumber].inputParameters
	);
	const currentInputParams = useSelector(state => state.modelData.inputParams[simulationNumber] || {}, isEqual);

	const initializeInputValues = useMemo(() => {
		return Object.entries(inputParametersDefinition).reduce((newObj, [key, input]) => {
			const currentValue = currentInputParams[key]?.value ?? input.defaultValue;
			newObj[key] = { value: currentValue, mappingName: input.mappingName, netlistUnit: input.netlistUnit };
			return newObj;
		}, {});
	}, [currentInputParams, inputParametersDefinition]);

	const [inputValues, setInputValues] = useState(initializeInputValues);
	const [validationErrors, setValidationErrors] = useState({});
	const dispatch = useDispatch();
	const defaultMappingName = useSelector(state => state.designDefinition.partDefinition.defaultMappingName);
	const wpn = useSelector(state => state.designDefinition.partDefinition.wpn);

	const handleCalculateAndRecord = async () => {
		await handleCalculate();
		handleEventRecord();
		sendAdobeEvent('Calculate', window.location.href, APP_NAME + ' | ' + defaultMappingName, null, simulationName, wpn);
	};

	useImperativeHandle(ref, () => ({ handleCalculateAndRecord }));

	useEffect(() => {
		if (props.onValidationErrorsChange) {
			props.onValidationErrorsChange(validationErrors);
		}
	}, [validationErrors, props]);

	useEffect(() => {
		setInputValues(initializeInputValues);
	}, [currentInputParams, initializeInputValues]);

	useEffect(() => {
		handleCalculate();
		inputParametersDefinition.forEach(input => {
			if (input.displayInput) {
				dispatch(
					setDisplayInput({
						mappingName: input.mappingName,
						heading: input.heading,
						simNumber: simulationNumber,
						value: input.defaultValue,
						unit: input.unit
					})
				);
			}
		});
		//eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (Object.keys(validationErrors).length === 0) {
			dispatch(setInputParams({ simulationNumber, params: inputValues }));
		}
	}, [inputValues, simulationNumber, validationErrors, dispatch]);

	const handleInputChange = (event, key, inputDefinition) => {
		const newValue = event.target.value;
		const { heading, mappingName, netlistUnit, minBoundary, maxBoundary, displayInput, unit } = inputDefinition;
		const updatedInputValues = { ...inputValues, [key]: { value: newValue, mappingName, netlistUnit } };
		setInputValues(updatedInputValues);
		dispatch(setInputParams(updatedInputValues));

		if (displayInput) {
			dispatch(
				setDisplayInput({
					mappingName: mappingName,
					heading: heading,
					simNumber: simulationNumber,
					value: newValue,
					unit: unit
				})
			);
		}

		const validationError = validateInput(newValue, key, minBoundary, maxBoundary);
		if (validationError) {
			setValidationErrors({ ...validationErrors, ...validationError });
		} else {
			const updatedErrors = { ...validationErrors };
			delete updatedErrors[key];
			setValidationErrors(updatedErrors);
		}
	};

	const validateInput = (newValue, key, minBoundary, maxBoundary) => {
		if (newValue < minBoundary || newValue > maxBoundary) {
			return { [key]: `Value must be between ${minBoundary} and ${maxBoundary}` };
		}
		return null;
	};

	const addNetlistUnit = (acc, { value, mappingName, netlistUnit }) => {
		if (netlistUnit) {
			acc[mappingName] = value + netlistUnit;
		} else {
			acc[mappingName] = value;
		}
		return acc;
	};

	const prepareSimulationData = () => {
		return Object.values(inputValues).reduce(addNetlistUnit, {});
	};

	const handleCalculate = async () => {
		if (Object.keys(validationErrors).length === 0) {
			dispatch(setLoading(simulationNumber));
			const inputData = prepareSimulationData();
			try {
				const simulationData = await api.runSimulation({
					simulationId: simulationNumber,
					parameters: inputData,
					netlistPath: netlistPath,
					chartDescriptions: chartsDefinition,
					boundariesPath: boundariesPath
				});
				dispatch(setSimulationData(simulationData));
			} catch (err) {
				console.error(err);
			}
			dispatch(setLoading([simulationNumber]));
		}
	};

	const handleEventRecord = () => {
		recordEvent(
			'recalculate',
			JSON.stringify({
				part: defaultMappingName,
				simulationName: simulationName,
				inputsValues: prepareSimulationData()
			})
		);
	};

	return (
		showInputParams && (
			<Box>
				<Typography variant="subtitle2" sx={{ mb: 2 }}>
					{inputHeading}:
				</Typography>

				<InputParametersContainer>
					{Object.entries(inputParametersDefinition).map(([key, inputDefinition]) => (
						<InputContainer key={key}>
							<TextField
								type="number"
								id={`outlined-basic-${key}`}
								label={ReactHtmlParser(inputDefinition.heading)}
								variant="standard"
								value={inputValues[key]?.value ?? ''}
								InputProps={{
									endAdornment: <InputAdornment position="end">{inputDefinition.unit}</InputAdornment>
								}}
								InputLabelProps={{
									style: {
										fontSize: '20px'
									},
									shrink: true
								}}
								onChange={e => handleInputChange(e, key, inputDefinition)}
								sx={{ width: '100%' }}
								error={!!validationErrors[key]}
								helperText={validationErrors[key]}
							/>
						</InputContainer>
					))}
				</InputParametersContainer>
			</Box>
		)
	);
});

export default InputParameters;
